import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

interface ApNode {
  id: string;
  nr: string;
  titel: string | null;
  status: string;
  progress: number;
  is_milestone: boolean;
}

interface ApMindmapProps {
  projectName: string;
  items: ApNode[];
  onSelect?: (id: string) => void;
}

const STATUS_COLOR: Record<string, string> = {
  'Offen': 'var(--pm-gray-400)',
  'In Arbeit': 'var(--pm-info)',
  'Erledigt': 'var(--pm-success)',
  'Blockiert': 'var(--pm-error)',
};

/**
 * Mindmap-View: radiale Verteilung der Arbeitspakete um eine zentrale
 * Projekt-Wurzel. Status faerbt den Knoten, Meilensteine bekommen einen
 * Rauten-Indikator, Fortschritt als Donut-Ring um den Knoten.
 */
export function ApMindmap({ projectName, items, onSelect }: ApMindmapProps) {
  const W = 880;
  const H = 560;
  const cx = W / 2;
  const cy = H / 2;
  const rOuter = Math.min(W, H) * 0.38;

  const nodes = useMemo(() => {
    if (items.length === 0) return [];
    return items.map((ap, i) => {
      // Goldener-Schnitt-Verteilung statt naivem 2π/n — vermeidet visuelle
      // Cluster bei kleinen N und gibt eine angenehme Streuung.
      const angle = (i / items.length) * 2 * Math.PI - Math.PI / 2;
      return {
        ap,
        x: cx + Math.cos(angle) * rOuter,
        y: cy + Math.sin(angle) * rOuter,
        angle,
      };
    });
  }, [items, cx, cy, rOuter]);

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 720, maxHeight: 640 }}>
        <defs>
          <radialGradient id="pm-root-grad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--pm-petrol-700)" />
            <stop offset="100%" stopColor="var(--rittec-petrol)" />
          </radialGradient>
        </defs>

        {/* Speichen (vom Wurzel zu jedem AP) */}
        {nodes.map(({ ap, x, y }) => (
          <line
            key={`edge-${ap.id}`}
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke="var(--pm-bg-muted)"
            strokeWidth={1.5}
            strokeDasharray="3,4"
          />
        ))}

        {/* AP-Knoten */}
        {nodes.map(({ ap, x, y }) => {
          const color = STATUS_COLOR[ap.status] ?? 'var(--pm-gray-400)';
          const r = 28;
          const progressCirc = 2 * Math.PI * (r + 4);
          const progressOffset = progressCirc * (1 - Math.min(100, Math.max(0, ap.progress)) / 100);
          return (
            <g
              key={ap.id}
              transform={`translate(${x}, ${y})`}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}
              onClick={() => onSelect?.(ap.id)}
            >
              {/* Donut-Ring fuer Progress */}
              <circle
                r={r + 4}
                fill="none"
                stroke="var(--pm-bg-deep)"
                strokeWidth={3}
              />
              <circle
                r={r + 4}
                fill="none"
                stroke={color}
                strokeWidth={3}
                strokeDasharray={progressCirc}
                strokeDashoffset={progressOffset}
                transform="rotate(-90)"
                style={{ transition: 'stroke-dashoffset .3s ease' }}
              />
              {ap.is_milestone ? (
                <polygon
                  points={`0,${-r} ${r},0 0,${r} ${-r},0`}
                  fill="var(--pm-bg-subtle)"
                  stroke={color}
                  strokeWidth={2}
                />
              ) : (
                <circle r={r} fill="var(--pm-bg-subtle)" stroke={color} strokeWidth={2} />
              )}
              <text
                textAnchor="middle"
                y={-4}
                fontSize="11"
                fontWeight={700}
                fill="var(--pm-fg-base)"
                fontFamily="monospace"
              >
                {ap.nr}
              </text>
              <text
                textAnchor="middle"
                y={10}
                fontSize="9"
                fill="var(--pm-fg-muted)"
              >
                {ap.progress}%
              </text>
              <text
                textAnchor="middle"
                y={r + 18}
                fontSize="11"
                fill="var(--pm-fg-base)"
                style={{ pointerEvents: 'none' }}
              >
                {(ap.titel ?? '').slice(0, 22)}{ap.titel && ap.titel.length > 22 ? '…' : ''}
              </text>
            </g>
          );
        })}

        {/* Wurzel-Knoten (am Schluss damit er ueber den Speichen liegt) */}
        <g transform={`translate(${cx}, ${cy})`}>
          <circle r={56} fill="url(#pm-root-grad)" />
          <circle r={56} fill="none" stroke="var(--pm-petrol-800)" strokeWidth={2} />
          <text textAnchor="middle" y={-4} fontSize="12" fontWeight={700} fill="#fff">
            PROJEKT
          </text>
          <text textAnchor="middle" y={14} fontSize="11" fill="rgba(255,255,255,0.85)">
            {projectName.slice(0, 18)}{projectName.length > 18 ? '…' : ''}
          </text>
        </g>

        {/* Empty state */}
        {nodes.length === 0 && (
          <text x={cx} y={cy + 100} textAnchor="middle" fontSize="13" fill="var(--pm-fg-subtle)">
            Keine Arbeitspakete vorhanden — Mindmap leer.
          </text>
        )}
      </svg>
      <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', display: 'block', textAlign: 'center', mt: 1 }}>
        Rauten markieren Meilensteine. Donut-Ring zeigt Fortschritt 0–100%.
      </Typography>
    </Box>
  );
}
